import React, { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { Drawer, CircularProgress, Modal, Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { emit } from "react-native-react-bridge/lib/web";
import "./HoldOrderDrawer.css";
import { holdCartList, updatecartStatus, dataRetriveWhenPageRefresh } from "../../../../../config/Services";
import { getMobilePos } from "../../../product/all-products/redux/Reducers/paginationReducer";
import { useSnackbar } from "../../../product/hooks/SnackbarProvider";
import { setHoldOrdersCount } from "../../../product/all-products/redux/Actions/holdOrdersCountActions";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import ShoppingBasketOutlinedIcon from "@mui/icons-material/ShoppingBasketOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

const RESTAURANT_TABS = ["All", "Dine In", "Delivery", "Takeaway"];

const DineInIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-utensils-crossed w-4 h-4" aria-hidden="true">
    <path d="m16 2-2.3 2.3a3 3 0 0 0 0 4.2l1.8 1.8a3 3 0 0 0 4.2 0L22 8"></path>
    <path d="M15 15 3.3 3.3a4.2 4.2 0 0 0 0 6l7.3 7.3c.7.7 2 .7 2.8 0L15 15Zm0 0 7 7"></path>
    <path d="m2.1 21.8 6.4-6.3"></path>
    <path d="m19 5-7 7"></path>
  </svg>
);

const TakeawayIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-shopping-bag w-4 h-4" aria-hidden="true">
    <path d="M16 10a4 4 0 0 1-8 0"></path>
    <path d="M3.103 6.034h17.794"></path>
    <path d="M3.4 5.467a2 2 0 0 0-.4 1.2V20a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6.667a2 2 0 0 0-.4-1.2l-2-2.667A2 2 0 0 0 17 2H7a2 2 0 0 0-1.6.8z"></path>
  </svg>
);

const DeliveryIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-bike w-4 h-4" aria-hidden="true">
    <circle cx="18.5" cy="17.5" r="3.5"></circle>
    <circle cx="5.5" cy="17.5" r="3.5"></circle>
    <circle cx="15" cy="5" r="1"></circle>
    <path d="M12 17.5V14l-3-3 4-3 2 3h2"></path>
  </svg>
);

const BADGE_CONFIG = {
  DINE_IN:     { label: "Dine In",   cls: "hd-badge-dine-in",  icon: DineInIcon },
  QSR_DINE_IN: { label: "Dine In",   cls: "hd-badge-dine-in",  icon: DineInIcon },
  DELIVERY:    { label: "Delivery",  cls: "hd-badge-delivery", icon: DeliveryIcon },
  TAKE_AWAY:   { label: "Takeaway",  cls: "hd-badge-takeaway", icon: TakeawayIcon },
  TAKEAWAY:    { label: "Takeaway",  cls: "hd-badge-takeaway", icon: TakeawayIcon },
  POS_ORDER:   { label: "POS Order", cls: "hd-badge-pos",      icon: TakeawayIcon },
};

const getBadgeConfig = (type) => {
  if (!type) return { label: "Order", cls: "hd-badge-pos", icon: TakeawayIcon };
  const upper = type?.toUpperCase();
  if (BADGE_CONFIG[upper]) return BADGE_CONFIG[upper];

  const label = type
    ?.toLowerCase()
    ?.split("_")
    ?.map((word) => word?.charAt(0).toUpperCase() + word?.slice(1))
    ?.join(" ");
  return { label, cls: "hd-badge-pos", icon: TakeawayIcon };
};

const TAB_TO_SUBORDER = {
  "Dine In":  "DINE_IN",
  "Delivery": "DELIVERY",
  "Takeaway": "TAKE_AWAY",
};

function toIST(dateStr) {
  if (!dateStr) return "";
  try {
    // Append Z if no timezone info so it's parsed as UTC
    const normalized = dateStr.endsWith("Z") || dateStr.includes("+") ? dateStr : dateStr + "Z";
    return new Date(normalized).toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return "";
  }
}

function productSummary(cartProducts = []) {
  return cartProducts
    ?.slice(0, 3)
    ?.map((p) => {
      const qty = p?.quantity > 1 ? `${p?.quantity}x ` : "";
      return `${qty}${p?.variantName || p?.productName || ""}`;
    })
    .join(", ");
}

const HoldOrderDrawer = ({ open, onClose }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const showSnackbar = useSnackbar();
  const isMobilePos = useSelector(getMobilePos);

  const [loader, setLoader] = useState(false);
  const [results, setResults] = useState([]);
  const [activeTab, setActiveTab] = useState("All");
  const [search, setSearch] = useState("");
  const [actionLoader, setActionLoader] = useState(null);
  const [page, setPage] = useState(1);
  const [discardModalOpen, setDiscardModalOpen] = useState(false);
  const [orderToDiscard, setOrderToDiscard] = useState(null);
  const [visibilityMode, setVisibilityMode] = useState(null);
  const modeRef = useRef(null);
  const requestRef = useRef(0);
  const openRef = useRef(open);
  openRef.current = open;
  const limit = 10;

  const appConfig = JSON.parse(localStorage.getItem("appConfig") || "{}");
  const holdCartVisibility = appConfig?.enabledFeatures?.HOLD_CART_VISIBLE;
  const defaultMode = holdCartVisibility ? "OUTLET" : "TERMINAL";
  const selectedMode = visibilityMode || defaultMode;
  const terminalName = localStorage.getItem("licenseName");
  const retailType = localStorage.getItem("retailType");
  const isRestaurant = retailType === "RESTAURANT";
  const TABS = isRestaurant ? RESTAURANT_TABS : ["All"];
  const retailUserDetails = JSON.parse(localStorage.getItem("retailUserDetails") || "{}");
  const firstName = retailUserDetails?.firstName;
  const uidx = retailUserDetails?.uidx;
  const locId = localStorage.getItem("locId");

  const fetchHoldOrders = useCallback((mode = modeRef.current) => {
    if (!openRef.current) return;
    const request = ++requestRef.current;
    setLoader(true);
    setResults([]);
    const payload = {
      locationId: [locId],
      cartStatus: ["HOLD"],
      ...(mode === "TERMINAL" ? { terminalName: [terminalName] } : {}),
    };
    holdCartList(payload)
      .then((res) => {
        if (!openRef.current || request !== requestRef.current) return;
        const data = res?.data?.data?.results || [];
        setResults(data);
        dispatch(setHoldOrdersCount(data?.length));
        setLoader(false);
      })
      .catch((err) => {
        if (!openRef.current || request !== requestRef.current) return;
        showSnackbar(err?.response?.data?.message || err?.message, "error");
        setLoader(false);
      });
  }, [locId, terminalName]);

  // Fetch when drawer opens — reset state synchronously so loader shows immediately
  useEffect(() => {
    if (open) {
      setActiveTab("All");
      setSearch("");
      setResults([]);
      setPage(1);
      setLoader(true);
      modeRef.current = defaultMode;
      setVisibilityMode(defaultMode);
      fetchHoldOrders(defaultMode);
    } else {
      modeRef.current = defaultMode;
      setVisibilityMode(null);
    }
    return () => { requestRef.current += 1; };
  }, [open, defaultMode, fetchHoldOrders]);

  const handleModeChange = (mode) => {
    if (mode === modeRef.current) return;
    modeRef.current = mode;
    setVisibilityMode(mode);
    setPage(1);
    fetchHoldOrders(mode);
  };

  useEffect(() => {
    setPage(1);
  }, [search, activeTab]);

  const handleAction = async (cartId, mobileNo, userName, type) => {
    try {
      setActionLoader({ cartId, type });
      const payload = {
        cartId,
        cartStatus: type === "delete" ? "USER_HOLD_DELETED" : "UN_HOLD",
        // comments: type === "delete" ? `Deleted by ${firstName}` : `Unhold by ${firstName}`,
        updatedByUser: uidx || null,
        updatedByName: firstName || null,
      };
      const res = await updatecartStatus(payload);
      if (type === "unhold") {
        const resolvedCartId = res?.data?.data?.cartId || cartId;
        const existing = JSON.parse(localStorage.getItem("customer_details") || "{}");
        const updated = {
          ...existing,
          name: userName || null,
          mobileNumber: mobileNo || null,
          uidx: res?.data?.data?.userId,
        };
        localStorage.setItem("customer_details", JSON.stringify(updated));
        localStorage.setItem("CartId", resolvedCartId);
        localStorage.removeItem("retrieveHoldCart");
        dispatch(setHoldOrdersCount(Math.max(0, results?.length - 1)));
        isMobilePos &&
          emit({ type: "localData", data: { CartId: resolvedCartId, customer_details: updated } });
        showSnackbar("Hold cart retrieved successfully", "success");
        onClose();
        if (retailType === "RESTAURANT") {
          const cartRes = await dataRetriveWhenPageRefresh(resolvedCartId).catch(() => null);
          const cartData = cartRes?.data?.data?.data || null;
          const subOrderType = cartData?.subOrderType;
          if (subOrderType === "DELIVERY" || subOrderType === "TAKE_AWAY") {
            localStorage.setItem("fineDineSaleMode", subOrderType);
          } else {
            localStorage.removeItem("fineDineSaleMode");
          }
          navigate("/restaurant/cart", { state: { holdCartData: cartData, holdCustomer: updated } });
        } else {
          // Fetch cart data to restore orderType flags before navigating
          const cartRes = await dataRetriveWhenPageRefresh(resolvedCartId).catch(() => null);
          const cartData = cartRes?.data?.data?.data || null;
          if (cartData?.orderType === "SALES_ORDER") {
            localStorage.setItem("cartOrderType", "SALES_ORDER");
          } else {
            localStorage.removeItem("cartOrderType");
          }
          navigate("/grocery/cart", { state: { holdCartData: cartData, holdCustomer: updated } });
        }
      } else {
        showSnackbar("Hold cart discarded successfully", "success");
        fetchHoldOrders();
      }
    } catch (err) {
      showSnackbar(err?.response?.data?.message || err?.message, "error");
    } finally {
      setActionLoader(null);
    }
  };

  const filtered = results.filter((r) => {
    if (activeTab !== "All") {
      const tabSubOrder = TAB_TO_SUBORDER[activeTab];
      const isDineInTab = tabSubOrder === "DINE_IN";
      const cartIsDineIn = r?.subOrderType === "DINE_IN" || r?.subOrderType === "QSR_DINE_IN";
      if (isDineInTab ? !cartIsDineIn : r?.subOrderType !== tabSubOrder) return false;
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      return (
        r?.cartId?.toLowerCase()?.includes(q) ||
        r?.tableNumber?.toString()?.includes(q) ||
        r?.userName?.toLowerCase()?.includes(q) ||
        r?.mobileNo?.includes(q) ||
        r?.createdByName?.toLowerCase()?.includes(q)
      );
    }
    return true;
  });

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      keepMounted={false}
      sx={{ zIndex: 1400 }}
      PaperProps={{ className: "hold-drawer-paper" }}
    >
      {/* Header */}
      <div className="hold-drawer-header-new">
        <div className="hold-drawer-header-inner">
          <h1 className="hold-drawer-header-title">Held Carts</h1>
          <div className="hold-drawer-scope-switch" role="group" aria-label="Held cart visibility">
            {[["TERMINAL", "Terminal"], ["OUTLET", "Outlet"]].map(([mode, label]) => (
              <button key={mode} type="button"
                className={`hold-drawer-tab-btn-new ${selectedMode === mode ? "active" : ""}`}
                aria-pressed={selectedMode === mode}
                onClick={() => handleModeChange(mode)}>
                {label}
              </button>
            ))}
          </div>
          <button
            className="hold-drawer-close-btn"
            data-testid="button-close-held-orders"
            onClick={(e) => { e.stopPropagation(); onClose(); }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-x h-5 w-5 text-slate-500" aria-hidden="true">
              <path d="M18 6 6 18"></path>
              <path d="m6 6 12 12"></path>
            </svg>
          </button>
        </div>

        {/* Toolbar */}
        <div className="hold-drawer-toolbar-new">
          <div className="hold-drawer-toolbar-inner">
            <div className="hold-drawer-tabs-new">
              {TABS?.map((tab) => {
                const isActive = activeTab === tab;
                const tabTestId = tab === "All" ? "button-filter-all-held-orders" :
                                  tab === "Dine In" ? "button-filter-held-orders-dine-in" :
                                  tab === "Delivery" ? "button-filter-held-orders-delivery" :
                                  tab === "Takeaway" ? "button-filter-held-orders-takeaway" : undefined;
                return (
                  <button
                    key={tab}
                    type="button"
                    className={`hold-drawer-tab-btn-new ${isActive ? "active" : ""}`}
                    data-testid={tabTestId}
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab}
                  </button>
                );
              })}
            </div>
            <div className="hold-drawer-search-new">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-search absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true">
                <path d="m21 21-4.34-4.34"></path>
                <circle cx="11" cy="11" r="8"></circle>
              </svg>
              <input
                className="hold-drawer-search-input-new"
                placeholder="Search name, phone, #id..."
                data-testid="input-search-held-orders"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="hold-drawer-body">
        {loader ? (
          <div className="hold-drawer-loader">
            <CircularProgress sx={{ color: "#2563eb" }} />
          </div>
        ) : filtered?.length === 0 ? (
          <div className="hold-drawer-empty">No hold orders found</div>
        ) : (
          <div className="hold-drawer-table-container">
            <div className="hold-drawer-table-header">
              <span>Order</span>
              <span>Name</span>
              <span>Items</span>
              <span>Type</span>
              <span>Time</span>
              <span style={{ textAlign: "right" }}>Value</span>
              <span style={{ textAlign: "center" }}>Actions</span>
            </div>
            <div className="hold-drawer-table-body">
              {filtered?.slice((page - 1) * limit, page * limit)?.map((r) => {
                const badge = getBadgeConfig(r?.subOrderType);
                const summary = productSummary(r?.cartProducts || []);
                const itemCount = r?.billing?.quantity || r?.cartProducts?.length || 0;
                const total = Number(r?.billing?.totalCartValue || 0);
                const isDiscarding = actionLoader?.cartId === r?.cartId && actionLoader?.type === "delete";
                const isRetrieving = actionLoader?.cartId === r?.cartId && actionLoader?.type === "unhold";
                const isActioning = isDiscarding || isRetrieving;
                const customerName = r?.userName || "NA";
                const customerPhone = r?.mobileNo || (r?.tableNumber ? `Table ${r?.tableNumber}` : "NA");

                return (
                  <div
                    key={r?.cartId}
                    className="hold-drawer-table-row"
                    data-testid={`row-held-order-${r?.cartId}`}
                  >
                    {/* Column 1: Order */}
                    <div>
                      <div className="hold-drawer-order-id-badge">
                        #{r?.cartId?.replace(/^[A-Za-z]+/, "")}
                      </div>
                    </div>

                    {/* Column 2: Name */}
                    <div style={{ minWidth: 0 }}>
                      <h3 className="hold-drawer-customer-name-text" data-testid={`text-held-order-customer-${r?.cartId}`}>
                        {customerName}
                      </h3>
                    </div>

                    {/* Column 3: Items */}
                    <div style={{ minWidth: 0 }}>
                      <p className="hold-drawer-item-count-text" data-testid={`text-held-order-count-${r?.cartId}`}>
                        {itemCount} Items
                      </p>
                    </div>

                    {/* Column 4: Type */}
                    <div>
                      <div className={`hold-drawer-type-badge ${badge.cls}`} data-testid={`badge-held-order-type-${r?.cartId}`}>
                        {badge?.icon}
                        {badge?.label}
                      </div>
                    </div>

                    {/* Column 5: Time */}
                    <div style={{ minWidth: 0 }}>
                      <div className="hold-drawer-time-cell">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-calendar-days h-4 w-4 shrink-0 text-slate-400" aria-hidden="true">
                          <path d="M8 2v4"></path>
                          <path d="M16 2v4"></path>
                          <rect width="18" height="18" x="3" y="4" rx="2"></rect>
                          <path d="M3 10h18"></path>
                          <path d="M8 14h.01"></path>
                          <path d="M12 14h.01"></path>
                          <path d="M16 14h.01"></path>
                          <path d="M8 18h.01"></path>
                          <path d="M12 18h.01"></path>
                          <path d="M16 18h.01"></path>
                        </svg>
                        <span className="hold-drawer-time-text" data-testid={`text-held-order-time-${r?.cartId}`}>
                          {toIST(r?.createdDate)}
                        </span>
                      </div>
                    </div>

                    {/* Column 6: Value */}
                    <div className="hold-drawer-value-cell">
                      <div className="hold-drawer-total-text" data-testid={`text-held-order-total-${r?.cartId}`}>
                        ₹{total.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                      </div>
                    </div>

                    {/* Column 7: Actions */}
                    <div className="hold-drawer-actions-cell">
                      <button
                        className="hd-btn-discard-table"
                         disabled={isActioning}
                         data-testid={`button-discard-held-order-${r?.cartId}`}
                         onClick={() => {
                           setOrderToDiscard(r);
                           setDiscardModalOpen(true);
                         }}
                       >
                        {isDiscarding ? (
                          <CircularProgress size={13} sx={{ color: "#ef4444" }} />
                        ) : (
                          <>
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-trash2 lucide-trash-2 mr-1.5 h-4 w-4" aria-hidden="true">
                              <path d="M10 11v6"></path>
                              <path d="M14 11v6"></path>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"></path>
                              <path d="M3 6h18"></path>
                              <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            </svg>
                           {/* Discard */}
                          </>
                        )}
                      </button>
                      <button
                        className="hd-btn-retrieve-table"
                        disabled={isActioning}
                        data-testid={`button-retrieve-held-order-${r?.cartId}`}
                        onClick={() => handleAction(r?.cartId, r?.mobileNo, r?.userName, "unhold")}
                      >
                        {isRetrieving ? (
                          <CircularProgress size={13} sx={{ color: "#fff" }} />
                        ) : (
                          <>
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-reply mr-1.5 h-4 w-4" aria-hidden="true">
                              <path d="M20 18v-2a4 4 0 0 0-4-4H4"></path>
                              <path d="m9 17-5-5 5-5"></path>
                            </svg>
                            Retrieve
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="hold-drawer-pagination">
              <span className="hold-drawer-pagination-text">
                {`${(page - 1) * limit + 1}-${Math.min(page * limit, filtered?.length)} of ${filtered?.length}`}
              </span>
              <div className="hold-drawer-pagination-btns">
                <button
                  type="button"
                  className="hold-drawer-page-btn"
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-chevron-left" aria-hidden="true" style={{ width: "16px", height: "16px" }}>
                    <path d="m15 18-6-6 6-6"></path>
                  </svg>
                </button>
                <button
                  type="button"
                  className="hold-drawer-page-btn"
                  disabled={page * limit >= filtered?.length}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-chevron-right" aria-hidden="true" style={{ width: "16px", height: "16px" }}>
                    <path d="m9 18 6-6-6-6"></path>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Discard Confirmation Modal */}
      <Modal
        open={discardModalOpen}
        onClose={() => setDiscardModalOpen(false)}
        sx={{ zIndex: 1500 }}
        className="discard-modal-overlay"
      >
        <Box className="hod-discard-modal-box">
          <button
            className="hod-discard-modal-close-btn"
            onClick={() => setDiscardModalOpen(false)}
            aria-label="Close"
            type="button"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-x"
              aria-hidden="true"
            >
              <path d="M18 6 6 18"></path>
              <path d="m6 6 12 12"></path>
            </svg>
          </button>
          
          <div className="hod-discard-modal-header">
            <h2 className="hod-discard-modal-title">Discard Order?</h2>
          </div>
          
          <div className="hod-discard-modal-actions">
            <button
              type="button"
              className="hod-discard-modal-btn-cancel"
              onClick={() => setDiscardModalOpen(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="hod-discard-modal-btn-confirm"
              onClick={() => {
                if (orderToDiscard) {
                  handleAction(orderToDiscard?.cartId, orderToDiscard?.mobileNo, orderToDiscard?.userName, "delete");
                }
                setDiscardModalOpen(false);
              }}
            >
              Discard Order
            </button>
          </div>
        </Box>
      </Modal>
    </Drawer>
  );
};

HoldOrderDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default HoldOrderDrawer;
